import { MenuItemOption } from "@components/common/mobile/mobile-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { toggleItemSelection } from "@utils/array-utils";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import {
  DeleteIcon,
  EmailIcon,
  InfoIcon,
  ResendEmailIcon,
  SelectionIcon,
  UserRoleIcon,
} from "@flat-ui";
import { useMembersUtils } from "@hooks/use-member-utils";
import { useMediaQueryList } from "@hooks/use-media-query";
import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { SdbProject } from "@custom-types/project-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { MemberRemoveMenuButton } from "@components/table/members/member-remove-menu-button";
import { MemberRoleMenuButton } from "@components/table/members/member-role-menu-button";
import { TeamRemoveMenuButton } from "@components/table/members/team-remove-menu-button";
import { useProjectRemoveMenuOption } from "@hooks/use-project-remove-menu-option";
import { useCompanyId } from "@context-providers/company-id-provider";
import { getMailHref } from "@utils/email-utils";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import {
  isGroupMember,
  isProjectMemberBase,
} from "@custom-types/member-type-guards";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { getCompanyByIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useResendInvitation } from "@hooks/use-resend-invitation";

interface UseMembersMenuOptionsParams {
  /** Context in which the members table is used */
  subjectType: MemberTableSubject;

  /** The selected project (for project member tables) */
  selectedProject?: SdbProject | null;

  /** The selected group (for group member tables) */
  group?: FaroCoreApiTypes.GroupDetailResponse | null;

  /** The selected team (for team member tables) */
  team?: FaroCoreApiTypes.Team;
}

/**
 * Returns menu options for the members list view (both desktop 3-dot menu and mobile).
 * Provides actions for selecting a member, viewing profile, changing role,
 * sending email, resending invitation, and removing member.
 */
export function useMembersMenuOptions({
  subjectType,
  selectedProject,
  group,
  team,
}: UseMembersMenuOptionsParams): MenuItemOption<SdbMemberTypes>[] {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { openProfilePage } = useMembersUtils();

  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const { isScreenSmOrSmaller } = useMediaQueryList();

  const selectedSdbCompany = useAppSelector(getCompanyByIdSelector(companyId));

  const { canDeleteMemberFromGroup } = useHasUserValidRoleGroupLevel({
    selectedGroup: group ?? null,
  });
  const {
    canDeleteMemberFromCompany,
    canDeleteMembersFromTeam,
    canEditMember,
    canResendInvitationToCompany,
  } = useHasUserValidRoleCompanyLevel();

  const { projectRemoveMenuOption, shouldShowProjectRemove } =
    useProjectRemoveMenuOption({ companyId, selectedProject });

  const { resendInvitation, shouldShowResendInvitation } = useResendInvitation({
    companyId,
    subjectType,
    selectedProject,
    canResendInvitationToCompany,
  });

  /** Whether the remove action should be shown for the given member */
  function shouldShowRemove(member: SdbMemberTypes): boolean {
    if (subjectType === "team" && team) {
      return canDeleteMembersFromTeam || canDeleteMemberFromCompany;
    }

    if (subjectType === "project") {
      return shouldShowProjectRemove(member);
    }

    if (
      group &&
      subjectType === "group" &&
      isGroupMember(member) &&
      canDeleteMemberFromGroup
    ) {
      return true;
    }

    if (
      subjectType === "workspace" &&
      selectedSdbCompany &&
      !isProjectMemberBase(member) &&
      canDeleteMemberFromCompany
    ) {
      return true;
    }

    return false;
  }

  return buildMenuOptions();

  /** Builds the full list of menu options */
  function buildMenuOptions(): MenuItemOption<SdbMemberTypes>[] {
    return [
      {
        label: "Select member",
        Icon: SelectionIcon,
        onSelect: (member) => {
          const newSelectedRowIds = toggleItemSelection(
            selectedRowIds,
            member.identity
          );
          dispatch(setInitialFetchingItems(newSelectedRowIds));
        },
        shouldShow: () => isScreenSmOrSmaller,
        shouldShowDividerAfter: isScreenSmOrSmaller,
      },
      {
        label: "Profile",
        Icon: InfoIcon,
        onSelect: (member) => {
          openProfilePage({
            member,
            source: "projects-menu-option",
          });
        },
        shouldShowDividerAfter: true,
      },
      ...getRoleMenuOption(),
      {
        label: "Send email",
        Icon: EmailIcon,
        onSelect: (member) => {
          window.location.href = getMailHref({ members: [member] });
        },
        shouldShowDividerAfter: (member) =>
          !shouldShowResendInvitation(member) && shouldShowRemove(member),
      },
      {
        label: "Resend invitation",
        Icon: ResendEmailIcon,
        onSelect: async (member) => {
          await resendInvitation(member);
        },
        shouldShow: shouldShowResendInvitation,
        shouldShowDividerAfter: (member) => shouldShowRemove(member),
      },
      ...getRemoveMenuOptions(),
    ];
  }

  /** Returns the role change menu option if the user has permission */
  function getRoleMenuOption(): MenuItemOption<SdbMemberTypes>[] {
    if (!canEditMember) {
      return [];
    }

    const shouldSupportRoleChange =
      subjectType === "workspace" ||
      subjectType === "project" ||
      subjectType === "group" ||
      subjectType === "team";

    if (!shouldSupportRoleChange) {
      return [];
    }

    return [
      {
        label: "Change role",
        Icon: UserRoleIcon,
        component: (member, onMenuClose) => (
          <MemberRoleMenuButton
            member={member}
            companyId={companyId}
            subjectType={subjectType}
            selectedProject={selectedProject}
            group={group}
            onMenuClose={onMenuClose}
          />
        ),
      },
    ];
  }

  /** Returns the remove menu option(s) based on the subject type */
  function getRemoveMenuOptions(): MenuItemOption<SdbMemberTypes>[] {
    if (subjectType === "team" && team) {
      return [
        {
          label: "Remove member",
          Icon: DeleteIcon,
          component: (member, onMenuClose) => (
            <TeamRemoveMenuButton
              companyId={companyId}
              team={team}
              member={member}
              onMenuClose={onMenuClose}
            />
          ),
          shouldShow: () =>
            canDeleteMembersFromTeam || canDeleteMemberFromCompany,
        },
      ];
    }

    if (subjectType === "project") {
      return projectRemoveMenuOption;
    }

    return [
      {
        label: "Remove member",
        Icon: DeleteIcon,
        component: (member, onMenuClose) => (
          <MemberRemoveMenuButton
            member={member}
            companyId={companyId}
            subjectType={subjectType}
            selectedProject={selectedProject}
            group={group}
            onMenuClose={onMenuClose}
          />
        ),
        shouldShow: shouldShowRemove,
      },
    ];
  }
}
